import { useState } from 'react'
import { X } from 'lucide-react'
import ImagePicker from '../../../ondemand-library/content-catalog/ImagePicker'

// AdSpeed creation methods, each with their own required field set.
const AD_TYPES = [
  { value: 'banner', label: 'Banner (image)',
    description: 'Image ad. Requires image URL + click URL.',
    fields: ['imageurl', 'clickurl', 'weight'] },
  { value: 'video', label: 'Video',
    description: 'Self-hosted video ad. Requires video URL.',
    fields: ['videourl', 'clickurl', 'skippable', 'weight'] },
  { value: 'vast', label: 'VAST Wrapper',
    description: 'External VAST tag URL. Most common for OTT.',
    fields: ['wrapperurl', 'skippable', 'pixelurl', 'descr', 'weight'] },
  { value: 'html', label: 'HTML',
    description: 'Custom HTML ad. Requires base64 HTML + dimensions.',
    fields: ['htmlbase64', 'width', 'height', 'weight'] },
  { value: 'text', label: 'Text',
    description: 'Plain-text ad with first/next lines + click URL.',
    fields: ['first', 'next', 'clickurl', 'weight'] },
]

const FIELD_LABELS = {
  imageurl:   'Image URL',
  videourl:   'Video URL',
  wrapperurl: 'VAST Wrapper URL',
  pixelurl:   'Pixel URL (impression beacon)',
  htmlbase64: 'HTML (base64 encoded)',
  clickurl:   'Click URL',
  first:      'First text line',
  next:       'Next text line(s)',
  descr:      'Description',
  width:      'Width (px)',
  height:     'Height (px)',
  weight:     'Weight (rotation priority)',
  skippable:  'Skippable',
}

const REQUIRED_BY_TYPE = {
  banner: ['imageurl', 'clickurl'],
  video:  ['videourl'],
  vast:   ['wrapperurl'],
  html:   ['htmlbase64', 'width', 'height'],
  text:   ['first', 'next', 'clickurl'],
}

// Edit-mode field list — every field AS.Ad.edit accepts per AdSpeed docs.
// NOTE: 'videourl' is intentionally absent — AS.Ad.edit does NOT accept it.
// Self-hosted video ads' videourl is set once at creation and can only be
// changed by deleting + recreating the ad.
const EDIT_FIELDS = [
  'status', 'weight',
  'descr',
  'clickurl', 'imageurl', 'pixelurl',
  'wrapperurl',     // VAST wrapper URL (AS.Ad.edit field: vastwrapperurl — backend remaps)
  'htmlbase64',
  'skippable',
  'width', 'height',
]

const STATUS_OPTIONS = [
  { value: '', label: '— Keep current —' },
  { value: 'active',   label: 'Active' },
  { value: 'pending',  label: 'Pending' },
  { value: 'archived', label: 'Archived' },
  { value: 'deleted',  label: 'Deleted' },
]

export default function AdEditor({ initial = null, onClose, onSubmit }) {
  const isEdit = !!initial
  const [type, setType] = useState(initial?.type || 'vast')
  const [name, setName] = useState(initial?.name || '')
  const [fields, setFields] = useState(() => {
    // Pre-fill from `initial` for edit mode. Pull every recognized creative key.
    const pre = {}
    if (!initial) return pre
    for (const k of [
      'clickurl', 'imageurl', 'videourl', 'wrapperurl', 'pixelurl',
      'htmlbase64', 'first', 'next', 'descr', 'skippable',
      'width', 'height', 'weight', 'status',
    ]) {
      if (initial[k] != null && initial[k] !== '') pre[k] = String(initial[k])
    }
    return pre
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const typeDef = AD_TYPES.find(t => t.value === type) || AD_TYPES[0]
  // `skippable` only applies to video / VAST ads. Hide it for banner / HTML /
  // text ads — AdSpeed rejects the entire edit call when skippable is set on
  // an incompatible type.
  const supportsSkippable = type === 'video' || type === 'vast'
  const displayFields = (isEdit ? EDIT_FIELDS : typeDef.fields)
    .filter(f => f !== 'skippable' || supportsSkippable)

  function setField(k, v) {
    setFields(f => ({ ...f, [k]: v }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (!name.trim() && !isEdit) return setError('Name is required')
    if (name.trim().length > 50) return setError('Name must be ≤ 50 characters')

    // Required-field check only on create.
    if (!isEdit) {
      const required = REQUIRED_BY_TYPE[type] || []
      for (const r of required) {
        if (!fields[r] || String(fields[r]).trim() === '') {
          return setError(`${FIELD_LABELS[r] || r} is required`)
        }
      }
    }

    const body = isEdit ? {} : { type }
    if (name.trim() && (!isEdit || name.trim() !== initial?.name)) {
      body.name = name.trim()
    }
    for (const f of displayFields) {
      const v = fields[f]
      if (v != null && String(v).trim() !== '') body[f] = v
    }

    setSaving(true)
    try {
      await onSubmit(body)
    } catch (err) {
      setError(err.message)
    }
    setSaving(false)
  }

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#121212', border: '1px solid #333', borderRadius: 10,
          padding: 24, minWidth: 520, maxWidth: 640, maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: '1.2rem' }}>
            {initial ? 'Edit Ad' : 'New Ad'}
          </h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: '#aaa', cursor: 'pointer',
          }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {!initial && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: '#aaa', marginBottom: 4 }}>
                Type
              </label>
              <select
                value={type}
                onChange={e => { setType(e.target.value); setFields({}) }}
                className="zones-search"
                style={{ width: '100%' }}
              >
                {AD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <div className="muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
                {typeDef.description}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', color: '#aaa', marginBottom: 4 }}>
              Name <span style={{ color: '#666' }}>(max 50 chars)</span>
            </label>
            <input
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              className="zones-search"
              style={{ width: '100%' }}
              maxLength={50}
              placeholder="e.g. PIHL-2026-Sponsor-AcmeAuto"
            />
          </div>

          {displayFields.map(f => {
            const required = !isEdit && (REQUIRED_BY_TYPE[type] || []).includes(f)
            if (f === 'status') {
              return (
                <div key={f} style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', color: '#aaa', marginBottom: 4 }}>
                    Status
                  </label>
                  <select
                    value={fields[f] || ''}
                    onChange={e => setField(f, e.target.value)}
                    className="zones-search"
                    style={{ width: '100%' }}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              )
            }
            if (f === 'skippable') {
              return (
                <div key={f} style={{ marginBottom: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', color: '#ddd' }}>
                    <input
                      type="checkbox"
                      checked={fields[f] === 'yes' || fields[f] === '1' || fields[f] === 'true'}
                      onChange={e => {
                        if (e.target.checked) {
                          setField(f, 'yes')
                        } else {
                          // Don't send skippable=no — AdSpeed rejects it as
                          // invalid. Just omit the field entirely.
                          setFields(prev => {
                            const { [f]: _drop, ...rest } = prev
                            return rest
                          })
                        }
                      }}
                    />
                    {FIELD_LABELS[f]}
                  </label>
                </div>
              )
            }
            const isTextArea = f === 'htmlbase64' || f === 'descr' || f === 'next'
            const isImageUrl = f === 'imageurl'
            return (
              <div key={f} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: '0.85rem', color: '#aaa', marginBottom: 4 }}>
                  {FIELD_LABELS[f] || f}
                  {required && <span style={{ color: '#f87171' }}> *</span>}
                </label>
                {isImageUrl ? (
                  <ImagePicker
                    value={fields[f] || ''}
                    onChange={v => setField(f, v)}
                    placeholder="https://… or browse Cloudflare Images"
                  />
                ) : isTextArea ? (
                  <textarea
                    value={fields[f] || ''}
                    onChange={e => setField(f, e.target.value)}
                    className="adtag-viewer"
                    style={{ width: '100%', minHeight: 80, padding: 8, background: '#1a1a1a', border: '1px solid #333', borderRadius: 6, color: '#eee', fontFamily: f === 'htmlbase64' ? 'ui-monospace, monospace' : undefined }}
                  />
                ) : (
                  <input
                    type={f === 'width' || f === 'height' || f === 'weight' ? 'number' : 'text'}
                    value={fields[f] || ''}
                    onChange={e => setField(f, e.target.value)}
                    className="zones-search"
                    style={{ width: '100%' }}
                  />
                )}
              </div>
            )
          })}

          {error && (
            <div className="sponsors-status error" style={{ marginBottom: 12 }}>{error}</div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" onClick={onClose} className="sponsors-refresh-btn">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn btn-primary">
              {saving ? 'Saving…' : (initial ? 'Save' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
